import React, { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import { Typography, Icon, TextArea } from '@scale/scaleui';
import { convertDate } from './helpers';
import { z } from 'zod';

const timestampSchema = z.object({
  seconds: z.number(),
});

const senderPersonSchema = z.object({
  displayName: z.string(),
  email: z.string(),
});

const messageSchema = z.object({
  messageId: z.string(),
  sender: z.string(),
  date: z.string(),
  body: z.string(),
  subject: z.string().optional(),
  messageUrl: z.string(),
  toRecipients: z.string(),
  ccRecipients: z.string(),
  labels: z.array(z.string()).optional(),
  senderPerson: senderPersonSchema,
  timestamp: timestampSchema,
});

export const emailSchema = z.object({
  subject: z.string(),
  messages: z.array(messageSchema),
  threadId: z.string(),
  threadUrl: z.string(),
});
export type Email = z.infer<typeof emailSchema>;

export enum EmailEditKey {
  Body = 'body',
  Subject = 'subject',
}

interface EmailViewProps {
  email: Email;
  useEditor?: boolean | undefined;
  attachmentIndex?: number;
  metadataIndex?: number;
  canEdit?: boolean | undefined;
  handleEmailEdit?: (
    newValue: string,
    editKey: EmailEditKey,
    attachmentIndex?: number,
    metadataIndex?: number,
  ) => void;
}

// Display Email with popup

export const EmailView: React.FC<EmailViewProps> = props => {
  const {
    email,
    useEditor = false,
    attachmentIndex,
    metadataIndex,
    canEdit,
    handleEmailEdit,
  } = props;
  const formattedMessages = useMemo(() => {
    return email.messages.map(message => {
      let body = message.body;
      const htmlRegex = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>.*?<\/\1>|<.*?\/>/;
      const bodyContainsHtml = htmlRegex.test(body);

      if (bodyContainsHtml) {
        body = sanitizeHtml(body, {
          allowedTags: sanitizeHtml.defaults.allowedTags.filter((o: string) => o !== 'a'),
          disallowedTagsMode: 'discard',
        });
      }

      return { ...message, body };
    });
  }, [email]);
  const formattedEmail = { ...email, messages: formattedMessages };

  return (
    <div className="space-y-2">
      {useEditor && handleEmailEdit ? (
        <TextArea
          key={email.threadId + metadataIndex + 'subject'}
          className="w-full"
          disabled={!canEdit}
          value={email.subject}
          rows={1}
          onChange={e => {
            handleEmailEdit(e.target.value, EmailEditKey.Subject, attachmentIndex, metadataIndex);
          }}
        />
      ) : (
        <Typography.H1>{email.subject}</Typography.H1>
      )}
      <div>
        {(useEditor ? email : formattedEmail).messages.map(message => {
          const outline = message.labels?.includes('STARRED') ? 'fas' : 'far';

          return (
            <div key={`key-${message.messageId}`} className="items-start mb-10">
              <div className="flex flex-row items-start w-full justify-between">
                <div className="flex flex-row items-start w-full justify-between space-x-6">
                  <Icon
                    size="lg"
                    icon={['fas', 'user']}
                    hasBackground
                    variant="white"
                    className="rounded-full"
                  />
                  <div className="flex flex-col">
                    {message.senderPerson.displayName} {`<${message.senderPerson.email}>`}
                    <div className="leading-0">
                      <Typography.Subtitle4>to: {message.toRecipients}</Typography.Subtitle4>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end w-full">
                  <div className="flex flex-row items-center w-full justify-end space-x-2">
                    <div>{convertDate(message.date, false)}</div>
                    <Icon size="md" icon={[outline, 'star']} variant="neutral" />
                  </div>
                </div>
              </div>

              <div className="mt-4" id="Email Body">
                {useEditor && handleEmailEdit ? (
                  <TextArea
                    key={email.threadId + metadataIndex + 'body'}
                    className="w-full"
                    disabled={!canEdit}
                    value={email.messages[0].body}
                    rows={24}
                    onChange={e => {
                      handleEmailEdit(
                        e.target.value,
                        EmailEditKey.Body,
                        attachmentIndex,
                        metadataIndex,
                      );
                    }}
                  />
                ) : (
                  <iframe srcDoc={message.body} className="w-full" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
